import React from 'react';
import { useTranslation } from 'react-i18next';
import { exactSubjectCount, levelOptions, subjectOptions, toggleSubject, type GoalDraft } from 'modules/studyPlan';
import { ChoiceCard } from './ChoiceCard';
import { GOAL_QUESTIONS, levelKeys, subjectKey } from './options';

interface GoalStepProps {
  draft: GoalDraft;
  onChange: (draft: GoalDraft) => void;
}

function Question({ id, title, hint, children }: { id: string; title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div>
        <h2 id={id} className="text-lg font-extrabold tracking-tight text-neutral-900 dark:text-white">{title}</h2>
        {hint && <p className="mt-1 text-sm text-neutral-500">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

/** One exam's own questions: its subjects or languages (pick any, DTM exactly two) and its grade, level or category (pick one). */
export function GoalStep({ draft, onChange }: GoalStepProps) {
  const { t } = useTranslation();
  const exam = draft.exam;
  const questions = GOAL_QUESTIONS[exam] ?? {};
  const levels = levelOptions(exam);
  const subjects = subjectOptions(exam);
  const exact = exactSubjectCount(exam);
  const full = exact !== null && draft.subjects.length >= exact;

  const levelQuestion = levels.length > 0 && (
    <Question id={`goal-${exam}-level`} title={questions.level ? t(questions.level) : ''}>
      {/* A long list of levels (the IELTS bands) sits in two columns, like the subjects. */}
      <div className={levels.length > 5 ? 'grid grid-cols-1 gap-2.5 sm:grid-cols-2' : 'space-y-2.5'}>
        {levels.map((level) => {
          const keys = levelKeys(exam, level);
          return (
            <ChoiceCard key={level} label={t(keys.label, { defaultValue: level })} sub={keys.sub && t(keys.sub)} selected={draft.level === level} onSelect={() => onChange({ ...draft, level })} />
          );
        })}
      </div>
    </Question>
  );

  const subjectQuestion = subjects.length > 0 && (
    <Question id={`goal-${exam}-subjects`} title={questions.subjects ? t(questions.subjects) : ''} hint={questions.hint ? t(questions.hint) : exact !== null ? t('learnerOb.hint_exact', { count: exact }) : t('learnerOb.hint_multiple')}>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {subjects.map((subject) => {
          const selected = draft.subjects.includes(subject);
          return (
            <ChoiceCard
              key={subject}
              label={t(subjectKey(exam, subject), { defaultValue: subject })}
              multiple
              selected={selected}
              disabled={!selected && full}
              onSelect={() => onChange(toggleSubject(draft, subject))}
            />
          );
        })}
      </div>
    </Question>
  );

  return (
    <div className="space-y-8">
      {/* DTM asks the grade first; attestation asks the subject first, since that is what its tests are about. */}
      {exam === 'DTM' ? (
        <>
          {levelQuestion}
          {subjectQuestion}
        </>
      ) : (
        <>
          {subjectQuestion}
          {levelQuestion}
        </>
      )}
    </div>
  );
}

export default GoalStep;
